/**
 * Operator-area shared pieces (plan §26).
 *
 * The operator console is deliberately thin: fleet state, provisioning runs, a safe retry, coupon
 * minting, and recent audit. §26 is explicit that raw customer note/corpus browsing does NOT
 * belong here — product operations must never require reading customer content — so nothing in
 * this area reaches for tenant data.
 *
 * These screens call the operator-gated `/api/admin/*` mirror of the service-token
 * `/internal/admin/*` surface (a browser cannot hold INTERNAL_SERVICE_TOKEN). It is mounted
 * (docs/API-CONTRACT.md §9); the "not available yet" fallback below stays as a safety net for a
 * deployment running an older API build.
 */
import { NavLink } from "react-router-dom";
import { ApiError } from "../../api/client.js";
import { Banner, ErrorNote } from "../../components/ui.js";

export function AdminNav() {
  return (
    <nav className="nav" aria-label="Operator" style={{ marginLeft: 0, marginBottom: 28 }}>
      <NavLink to="/admin" end>
        Deployments
      </NavLink>
      <NavLink to="/admin/coupons">Coupons</NavLink>
      <NavLink to="/admin/audit">Audit</NavLink>
    </nav>
  );
}

/**
 * Renders the right thing for an operator-surface failure: a plain notice when the endpoint is
 * simply not built yet, the ordinary error card otherwise.
 */
export function AdminError({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (error instanceof ApiError && (error.code === "not_implemented" || error.status === 404)) {
    return (
      <Banner kind="warn" title="Operator API not available yet">
        This console is built against the operator-gated <code>/api/admin/*</code> surface
        described in docs/API-CONTRACT.md §9. It is not mounted on this deployment.
      </Banner>
    );
  }
  if (error instanceof ApiError && error.code === "forbidden") {
    return (
      <Banner kind="error" title="Not permitted">
        Your account is not an operator.
      </Banner>
    );
  }
  return <ErrorNote error={error} {...(onRetry ? { onRetry } : {})} />;
}
